/**
 * Invoice List Component
 * Filterable, sortable table of invoices with status badges, actions, and search.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Search, Filter, ChevronDown, MoreHorizontal, Send, Download,
  Printer, Eye, Edit, XCircle, RefreshCw, ArrowUpDown, Link
} from 'lucide-react';
import {
  invoiceAPI,
  Invoice,
  InvoiceStatus,
  InvoiceListResponse,
  InvoiceFilters,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_COLORS,
  formatCents,
} from '@/services/api/invoiceAPI';

interface InvoiceListProps {
  organizationId: number;
  onView: (invoiceId: number, cboId?: number) => void;
  onEdit: (invoiceId: number, cboId?: number) => void;
  onCreate: () => void;
  onRefresh: () => void;
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  sent: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  viewed: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  void: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  uncollectible: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};

export function InvoiceList({ organizationId, onView, onEdit, onCreate, onRefresh }: InvoiceListProps) {
  const { fmt } = useFormattedDate();
  const [data, setData] = useState<InvoiceListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<InvoiceFilters>({
    page: 1,
    page_size: 25,
    sort_by: 'created_at',
    sort_dir: 'desc',
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!openActionMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionMenu]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const result = await invoiceAPI.listInvoices(organizationId, {
        ...filters,
        search: search || undefined,
        status: (statusFilter as InvoiceStatus) || undefined,
      });
      setData(result);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId, filters, search, statusFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleSort = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sort_by: field,
      sort_dir: prev.sort_by === field && prev.sort_dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const handleDownloadPdf = async (inv: Invoice) => {
    try {
      await invoiceAPI.openPdfInNewTab(inv.cbo_id || organizationId, inv.id);
    } catch (err) {
      console.error('Failed to download PDF:', err);
    }
    setOpenActionMenu(null);
  };

  const handleVoid = async (inv: Invoice) => {
    if (!confirm(`Are you sure you want to void invoice ${inv.invoice_number}?`)) return;
    try {
      await invoiceAPI.voidInvoice(inv.cbo_id || organizationId, inv.id);
      fetchInvoices();
      onRefresh();
    } catch (err) {
      console.error('Failed to void invoice:', err);
    }
    setOpenActionMenu(null);
  };

  const totalPages = data ? Math.ceil(data.total / (filters.page_size || 25)) : 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </form>

        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              setFilters(prev => ({ ...prev, page: 1 }));
            }}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">All Statuses</option>
            {Object.entries(INVOICE_STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <button
            onClick={() => { fetchInvoices(); onRefresh(); }}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 text-left">
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <button onClick={() => handleSort('invoice_number')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200">
                  Invoice # <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <button onClick={() => handleSort('invoice_date')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200">
                  Date <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <button onClick={() => handleSort('due_date')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200">
                  Due Date <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">
                <button onClick={() => handleSort('total_cents')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 ml-auto">
                  Amount <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Balance</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading && !data ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading invoices...
                </td>
              </tr>
            ) : data?.invoices.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                  <p className="text-sm">No invoices found</p>
                  <button
                    onClick={onCreate}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Create your first invoice
                  </button>
                </td>
              </tr>
            ) : (
              data?.invoices.map(inv => (
                <tr
                  key={inv.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer transition-colors"
                  onClick={() => onView(inv.id, inv.cbo_id)}
                >
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {inv.invoice_number}
                    </span>
                    {inv.portal?.url && (
                      <Link className="w-3 h-3 inline ml-1.5 text-green-500" title="Portal link active" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {inv.customer_snapshot?.name || 'Unknown'}
                    </div>
                    {inv.customer_snapshot?.company && (
                      <div className="text-xs text-gray-500">{inv.customer_snapshot.company}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE_CLASSES[inv.status] || STATUS_BADGE_CLASSES.draft}`}>
                      {INVOICE_STATUS_LABELS[inv.status] || inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {inv.status === 'draft' ? fmt.short(new Date().toISOString()) : inv.invoice_date ? fmt.short(inv.invoice_date) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {inv.status === 'draft' && inv.invoice_date && inv.due_date
                      ? (() => {
                          const offset = Math.round((new Date(inv.due_date + 'T00:00:00').getTime() - new Date(inv.invoice_date + 'T00:00:00').getTime()) / 86400000);
                          const due = new Date(); due.setDate(due.getDate() + offset);
                          return fmt.short(due.toISOString());
                        })()
                      : inv.due_date ? fmt.short(inv.due_date) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-gray-100">
                    {formatCents(inv.total_cents, inv.currency)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium">
                    <span className={inv.amount_due_cents > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                      {formatCents(inv.amount_due_cents, inv.currency)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right relative">
                    <button
                      onClick={e => { e.stopPropagation(); setOpenActionMenu(openActionMenu === inv.id ? null : inv.id); }}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </button>

                    {openActionMenu === inv.id && (
                      <div ref={menuRef} className="absolute right-4 top-full mt-1 z-50 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1">
                        <button
                          onClick={e => { e.stopPropagation(); onView(inv.id, inv.cbo_id); setOpenActionMenu(null); }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                        {inv.status === 'draft' && (
                          <button
                            onClick={e => { e.stopPropagation(); onEdit(inv.id, inv.cbo_id); setOpenActionMenu(null); }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Edit className="w-3.5 h-3.5" /> Edit
                          </button>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); handleDownloadPdf(inv); }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <Download className="w-3.5 h-3.5" /> Download PDF
                        </button>
                        {!['void', 'paid', 'uncollectible'].includes(inv.status) && (
                          <button
                            onClick={e => { e.stopPropagation(); handleVoid(inv); }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <XCircle className="w-3.5 h-3.5" /> Void
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total > (filters.page_size || 25) && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500">
            Showing {((filters.page || 1) - 1) * (filters.page_size || 25) + 1} to{' '}
            {Math.min((filters.page || 1) * (filters.page_size || 25), data.total)} of {data.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: (prev.page || 1) - 1 }))}
              disabled={(filters.page || 1) <= 1}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {filters.page || 1} of {totalPages}
            </span>
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
              disabled={(filters.page || 1) >= totalPages}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
